import { useMemo, useState } from "react";
import { Anchor, Sailboat, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { CONTACT, CITIES_DATA } from "@/lib/data";
import { CITY_COORDS } from "@/lib/cityCoords";

const TEAMS = [
  {
    id: "saint-malo",
    label: "Équipe Saint-Malo & Côte d'Émeraude",
    region: "Ille-et-Vilaine (35) · Côtes-d'Armor (22)",
    coverage: "25 villes couvertes",
    icon: Anchor,
    color: "#0F2C4F",
    deptCodes: ["35", "22"],
  },
  {
    id: "morbihan",
    label: "Équipe Sud Bretagne, Morbihan",
    region: "Morbihan (56) · Vilaine & Pénerf",
    coverage: "5 villes couvertes",
    icon: Sailboat,
    color: "#7A9A82",
    deptCodes: ["56"],
  },
];

const TEAM_BY_DEPT = {
  "35": TEAMS[0],
  "22": TEAMS[0],
  "56": TEAMS[1],
};

const TeamsBlock = () => {
  const [activeTeam, setActiveTeam] = useState("all");

  const cities = useMemo(() => {
    const all = Object.values(CITIES_DATA)
      .map((c) => ({ ...c, coords: CITY_COORDS[c.slug] }))
      .filter((c) => Array.isArray(c.coords));
    if (activeTeam === "all") return all;
    const team = TEAMS.find((t) => t.id === activeTeam);
    return all.filter((c) => team.deptCodes.includes(c.department));
  }, [activeTeam]);

  return (
    <section
      className="bg-cream py-20 lg:py-24 border-y border-gold/15"
      data-testid="teams-block"
    >
      <div className="container-tm">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-8 mb-10 items-end">
          <div className="lg:col-span-7">
            <span className="overline">Deux équipes locales</span>
            <h2 className="mt-4 font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Une équipe<span className="italic text-gold"> dédiée</span> à
              chaque <span className="italic text-sage">territoire</span>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-sans text-base text-ink/65 leading-relaxed">
              Deux équipes complètement distinctes, une au nord (Saint-Malo &
              Côte d'Émeraude), une au sud (Morbihan). Chaque propriétaire est
              suivi par l'équipe la plus proche de son bien.
            </p>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-7">
          <button
            type="button"
            data-testid="teams-filter-all"
            onClick={() => setActiveTeam("all")}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
              activeTeam === "all"
                ? "bg-ink text-cream border-ink"
                : "bg-white text-ink/70 border-gold/30 hover:border-gold hover:text-ink"
            }`}
          >
            Toutes les villes ({Object.keys(CITY_COORDS).length})
          </button>
          {TEAMS.map((t) => {
            const Icon = t.icon;
            const active = activeTeam === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`teams-filter-${t.id}`}
                onClick={() => setActiveTeam(t.id)}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
                  active
                    ? "bg-ink text-cream border-ink"
                    : "bg-white text-ink/70 border-gold/30 hover:border-gold hover:text-ink"
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: t.color }}
                />
                <Icon size={14} strokeWidth={1.8} />
                {t.label.replace("Équipe ", "")}
              </button>
            );
          })}
        </div>

        {/* Map */}
        <div
          className="rounded-3xl overflow-hidden shadow-soft border border-gold/20 bg-white mb-8"
          style={{ height: "480px" }}
          data-testid="teams-map"
        >
          <MapContainer
            center={[48.2, -2.4]}
            zoom={8}
            scrollWheelZoom={false}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {cities.map((c) => {
              const team = TEAM_BY_DEPT[c.department];
              return (
                <CircleMarker
                  key={c.slug}
                  center={c.coords}
                  radius={9}
                  pathOptions={{
                    color: team.color,
                    fillColor: team.color,
                    fillOpacity: 0.85,
                    weight: 2,
                  }}
                  eventHandlers={{
                    mouseover: (e) =>
                      e.target.setStyle({ radius: 14, fillOpacity: 1 }),
                    mouseout: (e) =>
                      e.target.setStyle({ radius: 9, fillOpacity: 0.85 }),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                    <span className="font-semibold">{c.name}</span>
                  </Tooltip>
                  <Popup>
                    <div className="font-sans" style={{ minWidth: 180 }}>
                      <p className="font-serif text-base font-bold text-ink mb-1">
                        {c.name}
                      </p>
                      <p className="text-xs text-ink/60 mb-2">
                        {team.label.replace("Équipe ", "Équipe : ")}
                      </p>
                      <Link
                        to={`/villes/${c.slug}`}
                        className="inline-flex items-center gap-1.5 text-gold hover:text-ink font-medium text-sm"
                      >
                        Voir la fiche <ArrowRight size={14} />
                      </Link>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>

        {/* Two team cards (compact, side by side) */}
        <div className="grid md:grid-cols-2 gap-5 lg:gap-6 mb-8">
          {TEAMS.map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.id}
                data-testid={`team-card-${t.id}`}
                className="bg-white rounded-2xl p-6 border border-ink/5 shadow-soft flex items-center gap-5"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-white"
                  style={{ backgroundColor: t.color }}
                >
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1">
                    {t.coverage}
                  </p>
                  <h3 className="font-serif text-lg text-ink leading-snug mb-0.5">
                    {t.label}
                  </h3>
                  <p className="font-sans text-xs text-ink/55 leading-relaxed">
                    {t.region}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Phone CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <p className="font-sans text-sm text-ink/65">
            Un seul numéro pour toutes les équipes :
          </p>
          <a
            href={CONTACT.phoneHref}
            data-testid="teams-block-phone"
            className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-ink/90 rounded-full px-5 py-2.5 transition-colors"
          >
            <Phone size={14} strokeWidth={2} />
            <span className="font-sans text-sm font-semibold">
              {CONTACT.phone}
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TeamsBlock;
